import React, { useState } from 'react';
import { PaperClipIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  buildWhatsAppAttachmentMediaKey,
  formatFileSize,
  getAttachmentLimit,
  headerFormatAcceptAttribute,
  isMediaHeaderFormat,
} from './whatsappPreview';
import { fetchPublicMediaByKey, uploadMediaAsset } from '../../services/MasterAdmin/mediaAssetService';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

export interface WhatsAppMediaHeaderValue {
  headerMediaUrl: string | null;
  headerMimeType: string | null;
  headerFileName: string | null;
}

interface WhatsAppMediaHeaderUploaderProps {
  headerFormat?: string | null;
  headerMediaUrl?: string | null;
  headerMimeType?: string | null;
  headerFileName?: string | null;
  onChange: (value: WhatsAppMediaHeaderValue) => void;
  disabled?: boolean;
  inputId?: string;
  compact?: boolean;
}

export const WhatsAppMediaHeaderUploader: React.FC<WhatsAppMediaHeaderUploaderProps> = ({
  headerFormat,
  headerMediaUrl,
  headerMimeType,
  headerFileName,
  onChange,
  disabled = false,
  inputId = 'whatsapp-header-media',
  compact = false,
}) => {
  const [uploading, setUploading] = useState(false);

  const isMedia = isMediaHeaderFormat(headerFormat);
  if (!isMedia) return null;

  const formatUpper = String(headerFormat || '').toUpperCase();
  const accept = headerFormatAcceptAttribute(headerFormat);
  const uploadId = `${inputId}-upload`;
  const replaceId = `${inputId}-replace`;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const limit = getAttachmentLimit(file.type || '');
    if (file.size > limit) {
      toast.error(
        `File "${file.name}" exceeds the ${formatFileSize(limit)} limit for WhatsApp ${formatUpper} headers.`
      );
      return;
    }

    setUploading(true);
    try {
      const key = buildWhatsAppAttachmentMediaKey(file);
      await uploadMediaAsset({ file, key, name: file.name });
      const pub = await fetchPublicMediaByKey(key);

      onChange({
        headerMediaUrl: pub.fileUrl || null,
        headerMimeType: pub.mimeType || file.type || null,
        headerFileName: file.name,
      });
      toast.success(`${formatUpper} header media ready`);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to upload header media'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemove = () => {
    onChange({
      headerMediaUrl: null,
      headerMimeType: null,
      headerFileName: null,
    });
  };

  return (
    <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/70 p-4 dark:border-gray-700 dark:bg-gray-800/40">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
            Header Media Attachment ({formatUpper})
          </h4>
          {!compact && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              This template requires a {formatUpper.toLowerCase()} header file. Upload once to use for all notifications sent with this event.
            </p>
          )}
        </div>
        <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-bold text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300">
          Max 16 MB
        </span>
      </div>

      {headerMediaUrl ? (
        <div className="mt-3 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 overflow-hidden text-xs">
              <PaperClipIcon className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <span className="truncate font-medium text-gray-800 dark:text-gray-200">
                {headerFileName || 'Header Media File'}
              </span>
            </div>

            <div className="flex items-center gap-1 shrink-0">
              <label
                htmlFor={replaceId}
                className="cursor-pointer rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                title="Replace Media"
              >
                <ArrowPathIcon className="h-4 w-4" />
                <input
                  id={replaceId}
                  type="file"
                  accept={accept}
                  onChange={handleFileSelect}
                  disabled={disabled || uploading}
                  className="hidden"
                />
              </label>
              <button
                type="button"
                onClick={handleRemove}
                disabled={disabled || uploading}
                className="rounded-md p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"
                title="Remove Media"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Media Preview Box */}
          {!compact && (
          <div className="relative overflow-hidden rounded-lg border border-gray-100 bg-gray-950/90 dark:border-gray-800">
            {formatUpper === 'IMAGE' && (
              <img
                src={headerMediaUrl}
                alt="Header Media Preview"
                className="max-h-48 w-full object-contain"
              />
            )}
            {formatUpper === 'VIDEO' && (
              <video
                src={headerMediaUrl}
                controls
                className="max-h-56 w-full rounded-md object-contain"
              />
            )}
            {formatUpper === 'DOCUMENT' && (
              <div className="flex items-center gap-3 p-4 text-white">
                <PaperClipIcon className="h-8 w-8 text-emerald-400" />
                <div>
                  <p className="text-xs font-semibold">{headerFileName || 'Document Attachment'}</p>
                  <a
                    href={headerMediaUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[11px] text-emerald-400 underline hover:text-emerald-300"
                  >
                    View Document
                  </a>
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      ) : (
        <div className="mt-3">
          <label
            htmlFor={uploadId}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white p-4 text-center transition hover:border-emerald-500 hover:bg-emerald-50/30 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-emerald-500 ${
              disabled || uploading ? 'pointer-events-none opacity-60' : ''
            }`}
          >
            <PaperClipIcon className="h-6 w-6 text-gray-400" />
            <span className="mt-1 text-xs font-medium text-gray-700 dark:text-gray-300">
              {uploading ? 'Uploading media…' : `Click to upload ${formatUpper.toLowerCase()} header media`}
            </span>
            <span className="mt-0.5 text-[11px] text-gray-400">
              Supports {accept.replace(/\./g, ' ').toUpperCase()} (Max 16 MB)
            </span>
            <input
              id={uploadId}
              type="file"
              accept={accept}
              onChange={handleFileSelect}
              disabled={disabled || uploading}
              className="hidden"
            />
          </label>
        </div>
      )}
    </div>
  );
};

export default WhatsAppMediaHeaderUploader;
